import { useState, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const PurchaseTransferChart = ({ purchaseTransferGraph }) => {
    const [timeRange, setTimeRange] = useState('monthly');

    // Sort data by year and month
    const sortedData = useMemo(() => {
        if (!purchaseTransferGraph || purchaseTransferGraph.length === 0) return [];
        return [...purchaseTransferGraph].sort((a, b) => {
            if (a.year !== b.year) {
                return a.year - b.year;
            }
            return a.month - b.month;
        });
    }, [purchaseTransferGraph]);

    // Get monthly data
    const monthlyData = useMemo(() => {
        if (timeRange === 'yearly') return [];
        return sortedData;
    }, [sortedData, timeRange]);

    // Get yearly data (aggregate by year)
    const yearlyData = useMemo(() => {
        if (timeRange === 'monthly') return [];
        const yearMap = {};

        sortedData.forEach(item => {
            if (!yearMap[item.year]) {
                yearMap[item.year] = {
                    year: item.year,
                    purchaseTotal: 0,
                    transferTotal: 0
                };
            }
            yearMap[item.year].purchaseTotal += item.purchaseTotal || 0;
            yearMap[item.year].transferTotal += item.transferTotal || 0;
        });

        return Object.values(yearMap).sort((a, b) => a.year - b.year);
    }, [sortedData, timeRange]);

    // Get month-year label
    const getMonthYearLabel = (month, year) => {
        const date = new Date(year, month - 1, 1);
        return date.toLocaleString('default', {
            month: 'short',
            year: '2-digit',
        });
    };

    // Prepare chart data
    const chartData = useMemo(() => {
        if (timeRange === 'monthly') {
            return {
                labels: monthlyData.map(item =>
                    getMonthYearLabel(item.month, item.year)
                ),
                datasets: [
                    {
                        label: 'Purchases',
                        data: monthlyData.map(item => item.purchaseTotal || 0),
                        borderColor: '#3B82F6',
                        backgroundColor: 'rgba(59, 130, 246, 0.08)',
                        tension: 0.4,
                        fill: true,
                        pointBackgroundColor: '#3B82F6',
                        pointBorderColor: '#fff',
                        pointBorderWidth: 2,
                        pointRadius: 4,
                    },
                    {
                        label: 'Transfers',
                        data: monthlyData.map(item => item.transferTotal || 0),
                        borderColor: '#10B981',
                        backgroundColor: 'rgba(16, 185, 129, 0.08)',
                        tension: 0.4,
                        fill: true,
                        pointBackgroundColor: '#10B981',
                        pointBorderColor: '#fff',
                        pointBorderWidth: 2,
                        pointRadius: 4,
                    }
                ]
            };
        } else {
            return {
                labels: yearlyData.map(item => item.year.toString()),
                datasets: [
                    {
                        label: 'Purchases',
                        data: yearlyData.map(item => item.purchaseTotal || 0),
                        borderColor: '#3B82F6',
                        backgroundColor: 'rgba(59, 130, 246, 0.08)',
                        tension: 0.4,
                        fill: true,
                        pointBackgroundColor: '#3B82F6',
                        pointBorderColor: '#fff',
                        pointBorderWidth: 2,
                        pointRadius: 4,
                    },
                    {
                        label: 'Transfers',
                        data: yearlyData.map(item => item.transferTotal || 0),
                        borderColor: '#10B981',
                        backgroundColor: 'rgba(16, 185, 129, 0.08)',
                        tension: 0.4,
                        fill: true,
                        pointBackgroundColor: '#10B981',
                        pointBorderColor: '#fff',
                        pointBorderWidth: 2,
                        pointRadius: 4,
                    }
                ]
            };
        }
    }, [monthlyData, yearlyData, timeRange]);

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        return `${context.dataset.label}: ₹${context.raw.toLocaleString()}`;
                    },
                    title: function (tooltipItems) {
                        if (timeRange === 'monthly') {
                            return tooltipItems[0].label;
                        }
                        return `Year: ${tooltipItems[0].label}`;
                    }
                }
            }
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: { color: '#6B7280', font: { size: 11 } }
            },
            y: {
                grid: { color: 'rgba(0,0,0,0.04)' },
                ticks: {
                    color: '#6B7280',
                    callback: v => `₹${v.toLocaleString()}`
                }
            }
        },
        interaction: {
            intersect: false,
            mode: 'index',
        },
    };

    // Calculate stats - same as ExpenseIncomeChart
    const totalPurchase = useMemo(() => {
        return sortedData.reduce((sum, item) => sum + (item.purchaseTotal || 0), 0);
    }, [sortedData]);

    const totalTransfer = useMemo(() => {
        return sortedData.reduce((sum, item) => sum + (item.transferTotal || 0), 0);
    }, [sortedData]);

    const hasData = sortedData.some(item => (item.purchaseTotal || 0) > 0 || (item.transferTotal || 0) > 0);

    return (
        <div className="bg-white rounded-xl border border-gray-100 px-4 py-4">
            <div className="flex justify-between items-center mb-4">
                <div>
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                        Purchase & Transfer Trends
                    </h3>
                    <p className="text-xs text-gray-400 mt-0.5">
                        {timeRange === 'monthly' ? 'Monthly view' : 'Yearly view'}
                    </p>
                </div>
                {/* <div className="flex items-center bg-gray-100 rounded-full p-0.5">
                    {['monthly', 'yearly'].map((range) => (
                        <button
                            key={range}
                            onClick={() => setTimeRange(range)}
                            className={`px-3 py-1 text-xs font-medium rounded-full transition-all
                                ${timeRange === range
                                    ? 'bg-white shadow-sm text-gray-900'
                                    : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            {range === 'monthly' ? 'Monthly' : 'Yearly'}
                        </button>
                    ))}
                </div> */}
            </div>

            <div className="h-64">
                {hasData ? (
                    <Line data={chartData} options={options} />
                ) : (
                    <div className="h-full flex items-center justify-center text-gray-500">
                        No data available for the selected period
                    </div>
                )}
            </div>

            {/* Stats Cards - Same as ExpenseIncomeChart */}
            <div className="mt-4 grid grid-cols-2 gap-4">
                <StatCard
                    label="Total Purchases"
                    value={totalPurchase}
                    color="text-blue-600"
                    formatCurrency
                />
                <StatCard
                    label="Total Transfers"
                    value={totalTransfer}
                    color="text-green-600"
                    formatCurrency
                />

            </div>
        </div>
    );
};

const StatCard = ({ label, value, color, formatCurrency = false }) => {
    const formattedValue = formatCurrency
        ? parseFloat(value || 0).toLocaleString('en-IN', {
            maximumFractionDigits: 0
        })
        : value;

    return (
        <div className="bg-gray-50 p-3 rounded-md hover:bg-gray-100 transition-colors">
            <p className="text-sm text-gray-500 font-medium uppercase truncate">{label}</p>
            <p className={`text-lg font-bold ${color}`}>
                {formatCurrency ? '₹' : ''}{formattedValue}
            </p>
        </div>
    );
};

export default PurchaseTransferChart;